import type { TutorialStep } from "./types.ts";

/**
 * 「繰り返し作成の設定と編集」チュートリアルのステップ定義。
 *
 * 大きく 3 部構成:
 *   第1部  タスクの詳細 → 「繰り返し」から自動作成ルールを設定し、
 *          「繰り返し作成一覧」で確認・編集できることを学ぶ。
 *   第2部  作成元タスクを編集すると「設定も更新しますか？」の
 *          プロンプトが表示されることを体験する。
 *   第3部  日付が経過して自動作成されたタスクを開き、
 *          「自動作成されました」の表示・ルール確認・編集反映を学ぶ。
 *
 * このチュートリアルでは練習用にプロジェクトとタスクを 1 件用意済み。
 * targetKey は SandboxScreen 側の data-tut 属性と対応する。
 */
export const RECURRENCE_TUTORIAL_STEPS: TutorialStep[] = [
  // ===== 第1部: 繰り返し作成を設定する =====
  {
    id: "welcome",
    targetKey: null,
    placement: "center",
    title: "繰り返し作成を学ぶ",
    body:
      "定例会議や月次レポートなど、決まったタイミングで繰り返すタスクを「自動で作成」できる機能を体験します。\n" +
      "ポイントは、繰り返し作成は「設定したときのタスク内容をコピーして」自動でタスクを作る、という点です。\n" +
      "「設定する → 一覧で確認・編集 → 自動作成されたタスクを開く」の流れを進めます。練習用のプロジェクトとタスクは用意済みです。",
  },
  {
    id: "select-task",
    targetKey: "task-row",
    placement: "bottom",
    title: "① タスクの詳細を開く",
    body: "繰り返しの元にしたいタスクの行をクリックして、右側の詳細パネルを開きましょう。",
    expectAction: "select-task",
  },
  {
    id: "open-recurrence",
    targetKey: "detail-recurrence-btn",
    placement: "top",
    title: "② 「繰り返し」を開く",
    body:
      "詳細パネルの下部にある「繰り返し」ボタンを押してください。\n" +
      "「自動作成ルール設定」ダイアログが開き、このタスクをもとにした繰り返しルールを確認・追加できます。",
    expectAction: "recurrence-open-dialog",
  },
  {
    id: "add-rule",
    targetKey: "recurrence-add-rule-btn",
    placement: "left",
    title: "③ ルールを追加する",
    body:
      "このダイアログには「設定した時点のタスク内容がコピーされる」という説明と、このタスクをもとに作成されたルールの一覧が表示されます。\n" +
      "まだルールがないので、「ルールを追加」を押して頻度を設定しましょう。",
    expectAction: "recurrence-show-form",
  },
  {
    id: "save-recurrence",
    targetKey: "recurrence-dialog",
    placement: "left",
    title: "④ 頻度を選んで保存する",
    body:
      "繰り返す頻度を選びます。「毎週」なら曜日、「毎月」なら日にちなど、頻度に応じた細かい指定ができます。\n" +
      "このほか、自動作成されるタスクの「期限」「リマインダー」「作成時の通知」もここでまとめて設定できます。\n" +
      "頻度を選んだら「保存する」を押しましょう。",
    expectAction: "recurrence-save",
  },
  {
    id: "explain-copy",
    targetKey: null,
    placement: "center",
    title: "内容はコピーして自動作成されます",
    body:
      "繰り返しを設定すると、「今のタスクの内容（タイトル・担当者・タグ・メモなど）」がコピーされ、指定した頻度で自動的に新しいタスクが作られます。\n" +
      "設定した内容は、あとから「繰り返し作成一覧」でいつでも確認・編集できます。次でその一覧を開いてみましょう。",
  },
  {
    id: "open-list",
    targetKey: "sidebar-recurrence-list",
    placement: "right",
    title: "⑤ 繰り返し作成一覧を開く",
    body: "画面左のサイドバーにある「繰り返し作成一覧」を押してください。",
    expectAction: "recurrence-open-list",
  },
  {
    id: "confirm-list",
    targetKey: "recurrence-list-dialog",
    placement: "left",
    title: "設定済みのルールを確認する",
    body:
      "左側にプロジェクトごとのルール一覧、右側に選択中ルールのタスク内容が表示されます。\n" +
      "右側では、自動作成されるタスクのタイトル・担当者・重要度・メモや頻度を編集できます。確認できたら「次へ」を押してください。",
  },
  {
    id: "edit-rule",
    targetKey: "rule-detail-priority",
    placement: "left",
    title: "⑥ ルールの内容を修正する",
    body:
      "右側の詳細で、ルールの内容を修正してみましょう。\n" +
      "ここでは「重要度」を変更してみてください。",
    expectAction: "recurrence-edit-rule",
  },
  {
    id: "explain-edit-rule",
    targetKey: "rule-detail-panel",
    placement: "left",
    title: "修正内容は今後の自動作成に反映されます",
    body:
      "ルールの内容を変更すると、その内容は保存され、以降に自動作成されるタスクへ反映されます。\n" +
      "すでに作成済みのタスクは変わりません。確認できたら「次へ」を押してください。",
  },
  {
    id: "close-list",
    targetKey: "recurrence-list-close",
    placement: "left",
    title: "一覧を閉じる",
    body: "「閉じる」を押して一覧を閉じ、次の操作に進みましょう。",
    expectAction: "recurrence-close-list",
  },

  // ===== 第2部: 作成元タスクを編集したとき =====
  {
    id: "edit-source",
    targetKey: "detail-priority",
    placement: "left",
    title: "⑦ 作成元のタスクを編集してみる",
    body:
      "繰り返しを設定した「作成元のタスク」を編集してみましょう。\n" +
      "ここでは「重要度」を変更してみてください。",
    expectAction: "recurrence-edit-source",
  },
  {
    id: "explain-prompt-source",
    targetKey: "update-prompt",
    placement: "left",
    title: "設定も更新しますか？",
    body:
      "作成元のタスクを編集すると、画面右下に「自動作成設定も更新しますか？」という確認が表示されます。\n" +
      "「更新する」を選ぶと、今後自動作成されるタスクにも同じ変更が反映されます。「しない」を選べば、このタスクだけの変更になります。\n" +
      "確認できたら「次へ」を押してください。",
  },

  // ===== 第3部: 自動作成された後 =====
  {
    id: "time-passed",
    targetKey: null,
    placement: "center",
    title: "⏳ 日付が経過しました",
    body:
      "設定した頻度のタイミングになり、ルールに沿って新しいタスクが「自動で作成」されました。\n" +
      "一覧に、繰り返しアイコン付きの新しいタスクが増えています。次でそのタスクを開いてみましょう。",
  },
  {
    id: "open-auto-task",
    targetKey: "auto-task-row",
    placement: "bottom",
    title: "⑧ 自動作成されたタスクを開く",
    body: "繰り返しアイコンが付いた、自動作成されたタスクの行をクリックしてください。",
    expectAction: "select-auto-task",
  },
  {
    id: "open-rule-from-badge",
    targetKey: "auto-created-badge",
    placement: "left",
    title: "⑨ 自動作成の表示を確認し、ルールを開く",
    body:
      "自動作成されたタスクを開くと、「このタスクは自動作成されました」という表示と、元になった繰り返しの頻度が確認できます。\n" +
      "「ルール編集」ボタンを押すと、このタスクの作成元ルールをすぐに確認・編集できます。押してみましょう。",
    expectAction: "recurrence-open-list",
  },
  {
    id: "confirm-rule",
    targetKey: "recurrence-list-dialog",
    placement: "left",
    title: "作成元のルールが開きました",
    body:
      "このタスクを自動作成したルールが選択された状態で開きます。\n" +
      "内容を変更すれば、以降に自動作成されるタスクへ反映されます。確認できたら「次へ」を押してください。",
  },
  {
    id: "close-list-2",
    targetKey: "recurrence-list-close",
    placement: "left",
    title: "一覧を閉じる",
    body: "「閉じる」を押して、最後の確認に進みましょう。",
    expectAction: "recurrence-close-list",
  },
  {
    id: "edit-auto",
    targetKey: "detail-priority",
    placement: "left",
    title: "⑩ 自動作成されたタスクを編集してみる",
    body:
      "自動作成されたタスクも、編集すると設定への反映を確認されます。\n" +
      "ここでも「重要度」を変更してみてください。",
    expectAction: "recurrence-edit-auto",
  },
  {
    id: "explain-prompt-auto",
    targetKey: "update-prompt",
    placement: "left",
    title: "作成されたタスクからも更新できます",
    body:
      "自動作成されたタスクを編集した場合も、「自動作成によって作られたタスクが編集されました。設定も更新しますか？」と確認されます。\n" +
      "作成元・自動作成タスクのどちらから編集しても、繰り返し設定に反映できるようになっています。",
  },
  {
    id: "done",
    targetKey: null,
    placement: "center",
    title: "お疲れさまでした！",
    body:
      "繰り返し作成は、タスク詳細の「繰り返し」から設定します。設定時の内容がコピーされ、指定した頻度で自動的にタスクが作られます。\n" +
      "設定済みのルールは「繰り返し作成一覧」から確認・編集でき、作成元タスク・自動作成タスクのどちらを編集しても設定へ反映できます。\n" +
      "「完了」を押すとチュートリアルを終了します。",
  },
];
